// services/firestore/licenseOrgs.ts — Read paths for the
// license_orgs/ collection. No write paths: orgs are created by
// the Stripe webhook and mutated by backend Cloud Functions only.

import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '@/services/firebase';
import type { LicenseOrg } from '@/types/domain';

function fromDoc(id: string, data: Record<string, unknown>): LicenseOrg {
  const seatCapacityRaw = data.seatCapacity;
  return {
    id,
    name: (data.name as string | undefined) ?? '',
    ownerUid: (data.ownerUid as string | undefined) ?? '',
    createdAt: (data.createdAt as LicenseOrg['createdAt']) ?? null,
    status:
      data.status === 'suspended' ? 'suspended' : 'active',
    // Tolerate missing field on legacy orgs and any future product
    // that doesn't declare a capacity. Treat zero / negative as
    // absent rather than rendering a "0 seats" capacity row.
    seatCapacity:
      typeof seatCapacityRaw === 'number' && seatCapacityRaw > 0
        ? Math.floor(seatCapacityRaw)
        : null,
  };
}

export async function listLicenseOrgs(): Promise<LicenseOrg[]> {
  const snap = await getDocs(collection(db, 'license_orgs'));
  return snap.docs.map((d) => fromDoc(d.id, d.data() ?? {}));
}

export async function getLicenseOrg(
  orgId: string,
): Promise<LicenseOrg | null> {
  const snap = await getDoc(doc(db, 'license_orgs', orgId));
  if (!snap.exists()) return null;
  return fromDoc(snap.id, snap.data() ?? {});
}
